import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { formatBytes, wireHeadersBytes, utf8ByteLength } from '../../utils/formatBytes';

function breakdownFromResponse(response) {
  if (response.byteMetrics) {
    const m = response.byteMetrics;
    return {
      responseHeaders: m.responseHeaders,
      responseBody: m.responseBody,
      requestHeaders: m.requestHeaders,
      requestBody: m.requestBody,
    };
  }
  const responseHeaders = wireHeadersBytes(response.headers);
  let responseBody;
  if (typeof response.size === 'number' && response.size >= 0) {
    responseBody = response.size;
  } else {
    const raw =
      typeof response.data === 'string'
        ? response.data
        : JSON.stringify(response.data ?? '');
    responseBody = utf8ByteLength(raw);
  }
  return {
    responseHeaders,
    responseBody,
    requestHeaders: 0,
    requestBody: 0,
  };
}

function Section({ icon: Icon, iconClass, title, total, headersBytes, bodyBytes }) {
  return (
    <div>
      <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-zinc-100">
        <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
        <span className="min-w-0 flex-1">{title}</span>
        <span className="shrink-0 tabular-nums">{formatBytes(total)}</span>
      </div>
      <div className="mt-2 space-y-1 pl-6 text-[11px] text-gray-500 dark:text-zinc-400">
        <div className="flex justify-between gap-4 tabular-nums">
          <span>Headers</span>
          <span>{formatBytes(headersBytes)}</span>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <span>Body</span>
          <span>{formatBytes(bodyBytes)}</span>
        </div>
      </div>
    </div>
  );
}

const MARGIN = 8;
const GAP = 12;

/**
 * Summary line shows total response transfer size; hover shows request/response header+body breakdown.
 * Renders the popover in a portal with fixed positioning so it is not clipped by panel overflow and
 * stays within the viewport when the trigger sits at the right edge.
 */
export default function TransferSizeHint({ response }) {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const { responseHeaders, responseBody, requestHeaders, requestBody } = breakdownFromResponse(response);
  const responseTotal = responseHeaders + responseBody;
  const requestTotal = requestHeaders + requestBody;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const pop = popoverRef.current;
    if (!trigger || !pop) return;
    const r = trigger.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;

    let left = r.right - pw;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - MARGIN - pw));

    let top = r.bottom + GAP;
    if (top + ph > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, r.top - ph - GAP);
    }

    setCoords({ top, left });
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, [cancelHide]);

  const show = useCallback(() => {
    cancelHide();
    const el = triggerRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      const approxW = Math.min(272, window.innerWidth - 2 * MARGIN);
      let left = r.right - approxW;
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - MARGIN - approxW));
      setCoords({ top: r.bottom + GAP, left });
    }
    setOpen(true);
  }, [cancelHide]);

  useLayoutEffect(() => {
    return () => cancelHide();
  }, [cancelHide]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition, responseTotal, requestTotal]);

  const popoverEl = (
    <div
      ref={popoverRef}
      role="tooltip"
      className="transfer-size-hint-popover"
      // Read-only text: it never takes the pointer, so where it overlaps
      // another control (the Variables panel's close button) that control
      // still gets the click (card e66fbe9b).
      style={{ top: coords.top, left: coords.left, pointerEvents: 'none' }}
    >
      <Section
        icon={ArrowDownCircle}
        iconClass="text-blue-500 dark:text-blue-400"
        title="Response Size"
        total={responseTotal}
        headersBytes={responseHeaders}
        bodyBytes={responseBody}
      />
      <div className="my-2 border-t border-gray-200 dark:border-zinc-700" />
      <Section
        icon={ArrowUpCircle}
        iconClass="text-amber-500 dark:text-amber-400"
        title="Request Size"
        total={requestTotal}
        headersBytes={requestHeaders}
        bodyBytes={requestBody}
      />
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        className="cursor-default border-b border-dotted border-gray-400 text-gray-600 tabular-nums dark:border-zinc-500 dark:text-zinc-300"
        aria-label="Response and request size breakdown"
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
      >
        {formatBytes(responseTotal)}
      </span>
      {open && typeof document !== 'undefined' ? createPortal(popoverEl, document.body) : null}
    </>
  );
}
